import axios from 'axios';
import {
  SessionState,
  SessionStateResponse,
  CreateSessionRequest,
  SendEmailRequest,
  SendSlackMessageRequest,
  SessionResult,
  MakeDecisionRequest,
  ScenarioDefinition
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// EdgeHub token management
let edgeHubToken: string | null = null;

export function setEdgeHubToken(token: string | null) {
  edgeHubToken = token;
}

// ROC-1237: the AUTHORITATIVE viewer token — the one set from the launch URL and
// actually used to fetch results. Prefer this over the Zustand store's edgeHubToken,
// which is persisted to sessionStorage and can be rehydrated with a STALE context
// (e.g. a manager who ran the sim as the candidate in this browser, then views the
// full report — the persisted candidate token would otherwise shadow their identity).
export function getEdgeHubToken(): string | null {
  return edgeHubToken ?? getLastLaunchParams()?.token ?? null;
}

// Request interceptor — attach EdgeHub JWT if present
apiClient.interceptors.request.use(
  (config) => {
    if (edgeHubToken) {
      config.headers['Authorization'] = `Bearer ${edgeHubToken}`;
    }
    const functionsKey = import.meta.env.VITE_FUNCTIONS_KEY;
    if (functionsKey) {
      config.headers['x-functions-key'] = functionsKey;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling.
//
// ROC-1229: we deliberately do NOT redirect a 401 back through EdgeHub's
// /login page. That "seamless re-auth" round-trip was the cause of the
// redirect loop between EdgePlay and EdgeHub:
//   - Assessment candidates have no EdgeHub account and must NEVER be asked
//     to sign in — bouncing them to /login and back (tokenless) looped forever.
//   - Academy learners hit the same loop when their access token had expired.
// Launch tokens are now long-lived (EdgeHub mints them with the EdgePlay launch
// lifetime), so a 401 during a normal session should not happen. If a token has
// genuinely expired (e.g. the user returns a day later), we surface the 401 to
// the calling view, which shows a graceful "reopen your link" / "return to
// EdgeHub" message instead of an infinite redirect. Re-clicking the magic link
// (candidates) or relaunching from EdgeHub (Academy) mints a fresh token and
// resumes the session.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Parse EdgeHub launch parameters from URL query string.
 * Called once on app init. Strips params from URL after reading.
 */
// Module-level cache of parsed launch params. Set once by parseEdgeHubLaunchParams,
// readable by any component via getLastLaunchParams. This survives Zustand persist
// hydration race conditions because it's a plain JS variable, not store state.
let _lastLaunchParams: { token: string; scenario: string; visibility: string } | null = null;

export function getLastLaunchParams() { return _lastLaunchParams; }

export function parseEdgeHubLaunchParams(): { token: string; scenario: string; visibility: string } | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const scenario = params.get('scenario');
  // EP-21b: candidate result visibility carried from EdgeHub ("None" | "ScoresOnly").
  const visibility = params.get('visibility');

  if (token) {
    // Strip sensitive params from URL without triggering navigation
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    url.searchParams.delete('scenario');
    url.searchParams.delete('visibility');
    url.searchParams.delete('resultsTier');
    window.history.replaceState({}, '', url.pathname + url.search);

    _lastLaunchParams = { token, scenario: scenario || '', visibility: visibility || '' };
    return _lastLaunchParams;
  }

  return null;
}

/**
 * Decode basic JWT claims without validation (validation happens server-side).
 */
export function decodeJwtClaims(token: string): { userId: string; name: string; email: string } | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return {
      userId: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || decoded.sub || '',
      name: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || decoded.name || '',
      email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || decoded.email || '',
    };
  } catch {
    return null;
  }
}

/**
 * True when the supplied EdgeHub JWT carries the Administrator role. Used to gate
 * admin-only UI (e.g. the diagnostics panel). Decodes the viewer's own token —
 * never the session's entitlementSource, since an admin may be viewing someone
 * else's session.
 */
export function isAdministratorToken(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.role === 'Administrator'
      || payload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'Administrator';
  } catch {
    return false;
  }
}

/**
 * ROC-1229: True when the token was minted for an Edge Assess candidate arriving
 * via a magic link (identityProvider = "assessment-magic-link"). Candidates have
 * no EdgeHub account and must never be sent to EdgeHub sign-in — used to tailor
 * the expired-token message (reopen your invitation email vs return to EdgeHub).
 */
export function isAssessmentMagicLinkToken(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.identityProvider === 'assessment-magic-link';
  } catch {
    return false;
  }
}

export const api = {
  // Session Management
  createSession: async (request: CreateSessionRequest): Promise<SessionState> => {
    const response = await apiClient.post<SessionState>('/sessions', request);
    return response.data;
  },

  startSession: async (sessionId: string): Promise<SessionState> => {
    const response = await apiClient.post<SessionState>(`/sessions/${sessionId}/start`);
    return response.data;
  },

  getSession: async (sessionId: string): Promise<SessionState> => {
    const response = await apiClient.get<SessionState>(`/sessions/${sessionId}`);
    return response.data;
  },

  getSessionState: async (sessionId: string): Promise<SessionStateResponse> => {
    const response = await apiClient.get<SessionStateResponse>(`/sessions/${sessionId}/state`);
    return response.data;
  },

  // Email Functions
  sendEmail: async (sessionId: string, emailRequest: SendEmailRequest): Promise<{ eventId: string; message: string }> => {
    const response = await apiClient.post<{ eventId: string; message: string }>(`/sessions/${sessionId}/email`, emailRequest);
    return response.data;
  },

  // Email Actions
  markEmailRead: async (sessionId: string, emailId: string): Promise<{ success: boolean; emailId: string }> => {
    const response = await apiClient.post<{ success: boolean; emailId: string }>(`/sessions/${sessionId}/email/${emailId}/read`);
    return response.data;
  },

  archiveEmail: async (sessionId: string, emailId: string): Promise<{ success: boolean; emailId: string }> => {
    const response = await apiClient.post<{ success: boolean; emailId: string }>(`/sessions/${sessionId}/email/${emailId}/archive`);
    return response.data;
  },

  flagEmail: async (sessionId: string, emailId: string): Promise<{ success: boolean; emailId: string }> => {
    const response = await apiClient.post<{ success: boolean; emailId: string }>(`/sessions/${sessionId}/email/${emailId}/flag`);
    return response.data;
  },

  // Slack Functions
  sendSlackMessage: async (sessionId: string, request: SendSlackMessageRequest): Promise<{ eventId: string; message: string }> => {
    const response = await apiClient.post<{ eventId: string; message: string }>(`/sessions/${sessionId}/slack`, request);
    return response.data;
  },

  // Phone Functions
  listenVoicemail: async (sessionId: string, voicemailId: string): Promise<{ success: boolean; voicemailId: string }> => {
    const response = await apiClient.post<{ success: boolean; voicemailId: string }>(`/sessions/${sessionId}/voicemail/${voicemailId}/listen`);
    return response.data;
  },

  // Account History Functions
  viewAccountRecord: async (sessionId: string, recordId: string): Promise<{ success: boolean; recordId: string }> => {
    const response = await apiClient.post<{ success: boolean; recordId: string }>(`/sessions/${sessionId}/account-records/${recordId}/view`);
    return response.data;
  },

  // Contact Functions
  viewContactProfile: async (sessionId: string, contactId: string): Promise<{ success: boolean; contactId: string; alreadyOpened: boolean }> => {
    const response = await apiClient.post<{ success: boolean; contactId: string; alreadyOpened: boolean }>(`/sessions/${sessionId}/contacts/${contactId}/profile-viewed`);
    return response.data;
  },

  // Scenario Functions
  getScenario: async (scenarioId: string): Promise<ScenarioDefinition> => {
    const response = await apiClient.get<ScenarioDefinition>(`/scenarios/${scenarioId}`);
    return response.data;
  },

  // Decision Functions
  makeDecision: async (sessionId: string, request: MakeDecisionRequest): Promise<{ decisionId: string; optionId: string; feedback: string }> => {
    const response = await apiClient.post<{ decisionId: string; optionId: string; feedback: string }>(`/sessions/${sessionId}/decisions`, request);
    return response.data;
  },

  // Session Control
  completeSession: async (sessionId: string): Promise<{ message: string; sessionId: string }> => {
    const response = await apiClient.post<{ message: string; sessionId: string }>(`/sessions/${sessionId}/complete`);
    return response.data;
  },

  // Results
  getSessionResults: async (sessionId: string): Promise<SessionResult> => {
    const response = await apiClient.get<SessionResult>(`/sessions/${sessionId}/results`);
    return response.data;
  },

  // Polling for updates (simple approach for MVP)
  getUpdates: async (sessionId: string, since?: string): Promise<SessionStateResponse> => {
    const params = since ? { since } : {};
    const response = await apiClient.get<SessionStateResponse>(`/sessions/${sessionId}/updates`, { params });
    return response.data;
  },
};

export default api;
